import { Box } from '@mui/material';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import AddressForm from '@sophocles/components/AddressForm';
import { FormSection } from '@sophocles/components/FormSection';
import { loadAddresses } from '@sophocles/features/warehouse/orders/api';
import { addressSlice } from '@sophocles/features/warehouse/orders/slice';

const DefaultAddress = {
  customer: null,
  site_group: null,
  address_1: '',
  address_2: '',
  city: '',
  state: '',
  zipcode: '',
  country: '',
  primary_address: true,
};

/**
 * Unified billing/shipping addresses component for customers and site groups.
 *
 * @param {Object} props
 * @param {number} props.customerId - Customer ID (for customer-level addresses)
 * @param {number} props.siteGroupId - Site group ID (for site group-level addresses)
 * @param {(payload: {billing, shipping}) => void} [props.onChange] - Fires on
 *   each user-initiated address edit (NOT on redux-load sync).  V2 callers use
 *   this to fold pending address edits into a parent's unified Save bar; the
 *   legacy callers rely on the ref imperative handle and don't need this.
 *
 * Use ref to access getBillingAddress() and getShippingAddress() methods.
 */
const BillingShippingAddresses = forwardRef(({ customerId, siteGroupId, onChange, variant }, ref) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Determine if this is for a customer or site group
  const isCustomerLevel = customerId != null && siteGroupId == null;

  // Build selector filter based on context
  const selectBillingAddress = state =>
    addressSlice.selectors.selectAllEntities(state).find(address => {
      if (!address.is_billing || !address.primary_address) return false;
      if (isCustomerLevel) {
        return address.customer == customerId && address.site_group == null;
      }
      return address.site_group == siteGroupId;
    });

  const selectShippingAddress = state =>
    addressSlice.selectors.selectAllEntities(state).find(address => {
      if (address.is_billing || !address.primary_address) return false;
      if (isCustomerLevel) {
        return address.customer == customerId && address.site_group == null;
      }
      return address.site_group == siteGroupId;
    });

  const currentBillingAddress = useSelector(selectBillingAddress);
  const currentShippingAddress = useSelector(selectShippingAddress);

  // Create default addresses with proper context
  const createDefaultBilling = () => ({
    ...DefaultAddress,
    is_billing: true,
    customer: isCustomerLevel ? customerId : null,
    site_group: isCustomerLevel ? null : siteGroupId,
  });

  const createDefaultShipping = () => ({
    ...DefaultAddress,
    is_billing: false,
    customer: isCustomerLevel ? customerId : null,
    site_group: isCustomerLevel ? null : siteGroupId,
  });

  const [billingAddress, setBillingAddress] = useState(currentBillingAddress ?? createDefaultBilling());
  const [shippingAddress, setShippingAddress] = useState(currentShippingAddress ?? createDefaultShipping());

  // Expose addresses to parent via ref
  useImperativeHandle(ref, () => ({
    getBillingAddress: () => billingAddress,
    getShippingAddress: () => shippingAddress,
  }));

  // Load addresses on mount
  useEffect(() => {
    if (isCustomerLevel) {
      dispatch(loadAddresses({ customer: customerId, site_group__isnull: true }));
    } else {
      dispatch(loadAddresses({ site_group: siteGroupId }));
    }
  }, [customerId, siteGroupId, dispatch, isCustomerLevel]);

  // Sync state when redux data changes
  useEffect(() => {
    setBillingAddress(currentBillingAddress ?? createDefaultBilling());
    setShippingAddress(currentShippingAddress ?? createDefaultShipping());
  }, [currentBillingAddress, currentShippingAddress]);

  // User-initiated edits funnel through these wrappers so we can
  // signal `onChange` (V2 unified Save bar) without firing it from
  // the redux-load sync useEffect above.
  const handleBillingEdit = next => {
    setBillingAddress(next);
    onChange?.({ billing: next, shipping: shippingAddress });
  };
  const handleShippingEdit = next => {
    setShippingAddress(next);
    onChange?.({ billing: billingAddress, shipping: next });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <FormSection label={t('customer.billingAddress')}>
        <AddressForm address={billingAddress} onChange={handleBillingEdit} variant={variant} />
      </FormSection>

      <FormSection label={t('customer.shippingAddress')}>
        <AddressForm address={shippingAddress} onChange={handleShippingEdit} variant={variant} />
      </FormSection>
    </Box>
  );
});

BillingShippingAddresses.displayName = 'BillingShippingAddresses';

export default BillingShippingAddresses;
